/**
 * Centralized Error Tracking Module
 * 
 * Provides a unified error tracking interface that works with both:
 * - Real Sentry error tracking (when VITE_SENTRY_DSN is set)
 * - Console-only logging (when DSN is missing)
 * 
 * This allows the app to work in preview/development environments
 * without requiring Sentry credentials.
 * 
 * IMPORTANT: All error tracking in the app must go through this module.
 * Do NOT call Sentry APIs directly anywhere else.
 */

import React from 'react';
import * as Sentry from '@sentry/react';

// Get Sentry DSN from Vite environment variables
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN || '';
const APP_ENV = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development';
const APP_VERSION = import.meta.env.VITE_APP_VERSION || '0.0.1';

// Check if Sentry is enabled (DSN is provided)
export const IS_SENTRY_ENABLED = SENTRY_DSN && SENTRY_DSN.trim() !== '';

/**
 * Initialize error tracking
 * Call this once at app startup
 */
export function initErrorTracking(): void {
  console.log('🚀 initErrorTracking() called');
  console.log('🔍 Checking environment variables:', {
    'import.meta.env.MODE': import.meta.env.MODE,
    'import.meta.env.VITE_SENTRY_DSN': import.meta.env.VITE_SENTRY_DSN ? `${String(import.meta.env.VITE_SENTRY_DSN).substring(0, 20)}...` : 'undefined',
    'import.meta.env.VITE_SENTRY_ENVIRONMENT': import.meta.env.VITE_SENTRY_ENVIRONMENT || 'undefined',
    'import.meta.env.VITE_APP_VERSION': import.meta.env.VITE_APP_VERSION || 'undefined',
    'SENTRY_DSN (computed)': SENTRY_DSN ? `${SENTRY_DSN.substring(0, 20)}...` : 'EMPTY',
    'APP_ENV (computed)': APP_ENV,
    'APP_VERSION (computed)': APP_VERSION,
    'IS_SENTRY_ENABLED': IS_SENTRY_ENABLED,
  });
  
  if (!IS_SENTRY_ENABLED) {
    console.warn('⚠️ Sentry DSN not configured. Error tracking disabled (console-only mode).');
    return;
  }

  try {
    console.log('🔧 Calling Sentry.init()...');
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: APP_ENV,
      release: `isoblock-cad@${APP_VERSION}`,
      
      // Performance Monitoring
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],
      
      // Performance sampling (lower in production to reduce quota usage)
      tracesSampleRate: APP_ENV === 'production' ? 0.1 : 1.0,
      
      // Session Replay sampling
      replaysSessionSampleRate: APP_ENV === 'production' ? 0.1 : 0.5,
      replaysOnErrorSampleRate: 1.0,
      
      // Error filtering
      beforeSend(event, hint) {
        console.log('📨 Sentry beforeSend called:', {
          eventId: event.event_id,
          message: event.message,
          exception: event.exception,
        });
        // In development, also log to console for debugging
        if (APP_ENV === 'development') {
          console.error('Sentry Error:', hint.originalException || hint.syntheticException);
        }
        return event;
      },
      
      // Ignore common benign errors
      ignoreErrors: [
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
        'Non-Error promise rejection captured',
        // Canvas-related errors that are expected during drawing
        'Canvas',
      ],
    });

    console.log('✅ Sentry initialized successfully');
  } catch (error) {
    console.error('❌ Failed to initialize Sentry:', error);
  }
}

/**
 * Capture an exception
 * Use this instead of calling Sentry.captureException directly
 */
export function captureException(
  error: unknown,
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
    level?: 'fatal' | 'error' | 'warning' | 'info' | 'debug';
  }
): void {
  console.log('📤 captureException called:', {
    IS_SENTRY_ENABLED,
    errorMessage: error instanceof Error ? error.message : String(error),
    tags: context?.tags,
    level: context?.level,
  });
  
  if (IS_SENTRY_ENABLED) {
    console.log('✅ Sentry is enabled, sending to Sentry...');
    try {
      Sentry.captureException(error, {
        tags: context?.tags,
        extra: context?.extra,
        level: context?.level || 'error',
      });
      console.log('✅ Sentry.captureException() called successfully');
    } catch (err) {
      console.error('❌ Error calling Sentry.captureException:', err);
    }
  } else {
    console.log('⚠️ Sentry is disabled, logging to console only');
    // Fallback: Log to console only
    console.error('[Error Tracking]', error, context);
  }
}

/**
 * Capture a message
 * Use this for non-error informational messages
 */
export function captureMessage(
  message: string,
  level: 'fatal' | 'error' | 'warning' | 'info' | 'debug' = 'info',
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
  }
): void {
  if (IS_SENTRY_ENABLED) {
    Sentry.captureMessage(message, {
      level,
      tags: context?.tags,
      extra: context?.extra,
    });
  } else {
    // Fallback: Log to console only
    const logMethod = level === 'error' || level === 'fatal' ? console.error : 
                      level === 'warning' ? console.warn : console.log;
    logMethod('[Error Tracking]', message, context);
  }
}

/**
 * Set user context for error tracking
 * Call this when user logs in/out
 */
export function setUser(user: {
  id: string;
  email?: string;
  username?: string;
} | null): void {
  if (IS_SENTRY_ENABLED) {
    if (user) {
      Sentry.setUser(user);
    } else {
      Sentry.setUser(null);
    }
  }
}

/**
 * Add breadcrumb for debugging
 * Use this to track user actions leading up to an error
 */
export function addBreadcrumb(
  message: string,
  data?: Record<string, unknown>,
  level: 'fatal' | 'error' | 'warning' | 'info' | 'debug' = 'info'
): void {
  if (IS_SENTRY_ENABLED) {
    Sentry.addBreadcrumb({
      message,
      data,
      level,
    });
  }
  // In dev mode, breadcrumbs are too noisy for console
}

/**
 * Set custom context
 * Use this to add additional context to all future errors
 */
export function setContext(name: string, context: Record<string, unknown> | null): void {
  if (IS_SENTRY_ENABLED) {
    Sentry.setContext(name, context);
  }
}

/**
 * Set custom tag
 * Use this to categorize errors
 */
export function setTag(key: string, value: string): void {
  if (IS_SENTRY_ENABLED) {
    Sentry.setTag(key, value);
  }
}

/**
 * Start a performance transaction
 * Use this to track performance of specific operations
 */
export function startTransaction(
  name: string,
  op: string
): { finish: () => void } | null {
  if (IS_SENTRY_ENABLED) {
    const transaction = Sentry.startTransaction({ name, op });
    return {
      finish: () => transaction.finish(),
    };
  }
  return null;
}

/**
 * Wrap a component with error boundary
 * Use this for critical UI sections that should not crash the entire app
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: React.ComponentType<{ error: Error; resetError: () => void }>
): React.ComponentType<P> {
  if (IS_SENTRY_ENABLED) {
    return Sentry.withErrorBoundary(Component, {
      fallback: fallback as any,
      showDialog: false,
    });
  }
  
  // Fallback: Return component as-is (no error boundary in dev mode)
  return Component;
}

/**
 * Get the Sentry ErrorBoundary component
 * Use this for manual error boundary placement
 */
export function getErrorBoundary() {
  if (IS_SENTRY_ENABLED) {
    return Sentry.ErrorBoundary;
  }
  
  // Fallback: Return a simple pass-through component (no error boundary in dev mode)
  return function FallbackErrorBoundary({ children }: { children: React.ReactNode }) {
    return children;
  };
}

/**
 * Wrap App with profiler (for performance monitoring)
 */
export function withProfiler<P extends object>(
  Component: React.ComponentType<P>
): React.ComponentType<P> {
  if (IS_SENTRY_ENABLED) {
    return Sentry.withProfiler(Component);
  }
  return Component;
}

/**
 * Send a test error to Sentry
 * Used to verify that error tracking is working correctly
 * This error will NOT be filtered out by ignoreErrors
 */
export function sendTestError(): void {
  console.log('🧪 sendTestError() called');
  console.log('📊 Environment Variables:', {
    SENTRY_DSN: SENTRY_DSN ? `${SENTRY_DSN.substring(0, 20)}...` : 'NOT SET',
    APP_ENV,
    APP_VERSION,
    IS_SENTRY_ENABLED,
  });
  
  const testError = new Error('Sentry Test Error - This is a deliberate test error to verify error tracking is working correctly');
  
  const context = {
    tags: {
      type: 'test',
      source: 'help-dialog',
    },
    extra: {
      timestamp: new Date().toISOString(),
      environment: APP_ENV,
      userAgent: navigator.userAgent,
    },
    level: 'info' as const,
  };
  
  console.log('🔧 Calling captureException with context:', context);
  
  // Add custom context to make it identifiable in Sentry
  captureException(testError, context);
  
  // Also log to console
  console.log('✅ Test error sent to Sentry (or console in dev mode)');
}
